import { useState } from "react";
import {
  Plus,
  Save,
  Trash2,
  MapPin,
  Clock,
  Mail,
  Loader2,
  Banknote,
} from "lucide-react";
import { toast } from "sonner";
import {
  useLabs,
  useCreateLab,
  useUpdateLab,
  useDeleteLab,
  useLabPrices,
  useCreateLabPrice,
  useUpdateLabPrice,
  useDeleteLabPrice,
} from "@/hooks/api";
import type { Lab, LabPrice } from "@/lib/api_client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface LabFormState {
  name: string;
  address: string;
  city: string;
  openingHours: string;
  manualBookingText: string;
  bookingUrl: string;
  snailMailRequisition: boolean;
  public: boolean;
  latitude: string;
  longitude: string;
}

function labToForm(lab: Lab): LabFormState {
  return {
    name: lab.name,
    address: lab.address,
    city: lab.city,
    openingHours: lab.openingHours,
    manualBookingText: lab.manualBookingText,
    bookingUrl: lab.bookingUrl ?? "",
    snailMailRequisition: lab.snailMailRequisition,
    public: lab.public,
    latitude: lab.latitude != null ? String(lab.latitude) : "",
    longitude: lab.longitude != null ? String(lab.longitude) : "",
  };
}

interface LocalLabPrice {
  id: string;
  labId: string;
  price: string;
  source: string;
  isNew?: boolean;
}

export function LabsPage() {
  const { data: labs, isLoading } = useLabs();
  const { data: allLabPrices, isLoading: pricesLoading } = useLabPrices();
  const createLab = useCreateLab();
  const updateLab = useUpdateLab();
  const deleteLab = useDeleteLab();
  const createLabPrice = useCreateLabPrice();
  const updateLabPrice = useUpdateLabPrice();
  const deleteLabPrice = useDeleteLabPrice();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<LabFormState | null>(null);
  const [localPrices, setLocalPrices] = useState<LocalLabPrice[]>([]);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const pricesForLab = (labId: string) =>
    (allLabPrices ?? []).filter((p) => p.labId === labId);

  const handleEdit = (lab: Lab) => {
    setEditingId(lab.id);
    setForm(labToForm(lab));
    setLocalPrices(
      pricesForLab(lab.id).map((p) => ({
        id: p.id,
        labId: p.labId,
        price: String(p.price),
        source: String(p.source),
      })),
    );
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm(null);
    setLocalPrices([]);
  };

  const handleSave = async (id: string) => {
    if (!form) return;
    await updateLab.mutateAsync({
      id,
      data: {
        ...form,
        bookingUrl: form.bookingUrl !== "" ? form.bookingUrl : null,
        latitude: form.latitude !== "" ? Number(form.latitude) : null,
        longitude: form.longitude !== "" ? Number(form.longitude) : null,
      },
    });

    // Sync prices
    const existing = pricesForLab(id);
    const existingIds = new Set(existing.map((p) => p.id));
    const localIds = new Set(localPrices.map((p) => p.id));

    for (const lp of localPrices) {
      if (existingIds.has(lp.id)) {
        await updateLabPrice.mutateAsync({
          id: lp.id,
          data: { price: lp.price, source: lp.source },
        });
      } else {
        await createLabPrice.mutateAsync({
          labId: id,
          price: lp.price,
          source: lp.source,
        });
      }
    }
    for (const ep of existing) {
      if (!localIds.has(ep.id)) {
        await deleteLabPrice.mutateAsync(ep.id);
      }
    }

    setEditingId(null);
    setForm(null);
    setLocalPrices([]);
    toast.success("Lab lagret");
  };

  const handleCreate = async () => {
    const lab = await createLab.mutateAsync({
      name: "Ny lab",
      address: "",
      city: "",
      openingHours: "Man-Fre 08:00-15:00",
      manualBookingText: "",
      bookingUrl: null,
      snailMailRequisition: false,
      public: false,
    });
    setEditingId(lab.id);
    setForm(labToForm(lab));
    setLocalPrices([]);
  };

  const handleDelete = async (id: string) => {
    await deleteLab.mutateAsync(id);
    if (editingId === id) handleCancel();
    toast.success("Lab slettet");
  };

  const updateField = (field: keyof LabFormState, value: string | boolean) => {
    setForm((f) => (f ? { ...f, [field]: value } : f));
  };

  const handleAddPrice = (labId: string) => {
    setLocalPrices((prev) => [
      ...prev,
      { id: `new-${Date.now()}`, labId, price: "0", source: "", isNew: true },
    ]);
  };

  const handlePriceChange = (
    idx: number,
    field: "price" | "source",
    value: string,
  ) => {
    setLocalPrices((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p)),
    );
  };

  const handleDeletePrice = (idx: number) => {
    setLocalPrices((prev) => prev.filter((_, i) => i !== idx));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-admin-text-subtle" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={handleCreate} size="sm" disabled={createLab.isPending}>
          <Plus className="h-4 w-4" />
          Legg til lab
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {(labs ?? []).map((lab) => {
          const isEditing = editingId === lab.id;
          const prices = pricesForLab(lab.id);
          return (
            <Card key={lab.id}>
              <CardContent className="p-4 space-y-3">
                {isEditing && form ? (
                  <>
                    <Field label="Navn">
                      <Input
                        value={form.name}
                        onChange={(e) => updateField("name", e.target.value)}
                      />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Adresse">
                        <Input
                          value={form.address}
                          onChange={(e) =>
                            updateField("address", e.target.value)
                          }
                        />
                      </Field>
                      <Field label="By">
                        <Input
                          value={form.city}
                          onChange={(e) => updateField("city", e.target.value)}
                        />
                      </Field>
                    </div>
                    <Field label="Åpningstider">
                      <Input
                        value={form.openingHours}
                        onChange={(e) =>
                          updateField("openingHours", e.target.value)
                        }
                      />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Breddegrad (lat)">
                        <Input
                          type="number"
                          step="any"
                          value={form.latitude}
                          onChange={(e) =>
                            updateField("latitude", e.target.value)
                          }
                          placeholder="59.9139"
                        />
                      </Field>
                      <Field label="Lengdegrad (lng)">
                        <Input
                          type="number"
                          step="any"
                          value={form.longitude}
                          onChange={(e) =>
                            updateField("longitude", e.target.value)
                          }
                          placeholder="10.7522"
                        />
                      </Field>
                    </div>
                    {form.latitude !== "" &&
                      form.longitude !== "" &&
                      !isNaN(Number(form.latitude)) &&
                      !isNaN(Number(form.longitude)) && (
                        <MapPreview
                          lat={Number(form.latitude)}
                          lng={Number(form.longitude)}
                        />
                      )}
                    <Field label="Bestillingstekst">
                      <Textarea
                        value={form.manualBookingText}
                        onChange={(e) =>
                          updateField("manualBookingText", e.target.value)
                        }
                        className="min-h-[80px]"
                      />
                    </Field>
                    <Field label="Bestillingslenke (URL)">
                      <Input
                        type="url"
                        value={form.bookingUrl}
                        onChange={(e) =>
                          updateField("bookingUrl", e.target.value)
                        }
                        placeholder="https://..."
                      />
                    </Field>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.snailMailRequisition}
                        onChange={(e) =>
                          updateField("snailMailRequisition", e.target.checked)
                        }
                        className="rounded accent-admin-primary"
                      />
                      <Mail className="h-3.5 w-3.5 text-admin-text-subtle" />
                      Brevrekvisisjon
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.public}
                        onChange={(e) =>
                          updateField("public", e.target.checked)
                        }
                        className="rounded accent-admin-primary"
                      />
                      Tilgjenglig for alle kunder
                    </label>

                    {/* Prices section */}
                    <div className="border-t border-admin-card-border pt-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-admin-text-subtle">
                          Priser
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAddPrice(lab.id)}
                          className="h-6 text-xs px-2"
                        >
                          <Plus className="h-3 w-3" />
                          Legg til
                        </Button>
                      </div>
                      {localPrices.length === 0 && (
                        <p className="text-xs text-admin-text-subtle">
                          Ingen priser lagt til
                        </p>
                      )}
                      {localPrices.map((price, idx) => (
                        <div key={price.id} className="flex items-center gap-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={price.price}
                            onChange={(e) =>
                              handlePriceChange(idx, "price", e.target.value)
                            }
                            placeholder="Pris (NOK)"
                            className="flex-1"
                          />
                          <Input
                            value={price.source}
                            onChange={(e) =>
                              handlePriceChange(idx, "source", e.target.value)
                            }
                            placeholder="Kilde"
                            className="w-36"
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeletePrice(idx)}
                            className="text-admin-negative hover:text-admin-negative h-8 w-8 shrink-0"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-2 pt-1">
                      <Button
                        size="sm"
                        onClick={() => handleSave(lab.id)}
                        disabled={updateLab.isPending}
                      >
                        <Save className="h-3 w-3" />
                        Lagre
                      </Button>
                      <Button size="sm" variant="ghost" onClick={handleCancel}>
                        Avbryt
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{lab.name}</h3>
                        {!lab.public && (
                          <span className="text-xs bg-admin-slate/50 text-admin-text-subtle px-1.5 py-0.5 rounded">
                            Intern
                          </span>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(lab)}
                          disabled={pricesLoading}
                          className="text-xs h-7"
                        >
                          Rediger
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteConfirmId(lab.id)}
                          className="text-admin-negative hover:text-admin-negative h-7 w-7"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-1.5 text-sm text-admin-text-secondary">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-3.5 w-3.5 text-admin-text-subtle shrink-0" />
                        {lab.address}, {lab.city}
                        {lab.latitude != null && lab.longitude != null && (
                          <span className="text-xs text-admin-text-subtle">
                            ({lab.latitude.toFixed(4)},{" "}
                            {lab.longitude.toFixed(4)})
                          </span>
                        )}
                      </div>
                      {lab.latitude != null && lab.longitude != null && (
                        <MapPreview lat={lab.latitude} lng={lab.longitude} />
                      )}
                      <div className="flex items-center gap-2">
                        <Clock className="h-3.5 w-3.5 text-admin-text-subtle shrink-0" />
                        {lab.openingHours}
                      </div>
                      {lab.snailMailRequisition && (
                        <div className="flex items-center gap-2">
                          <Mail className="h-3.5 w-3.5 text-admin-text-subtle shrink-0" />
                          Brevrekvisisjon
                        </div>
                      )}
                      {prices.length > 0 && (
                        <div className="flex items-start gap-2">
                          <Banknote className="h-3.5 w-3.5 text-admin-text-subtle shrink-0 mt-0.5" />
                          <div className="space-y-0.5">
                            {prices.map((p) => (
                              <div key={p.id}>
                                {Number(p.price).toFixed(2)} kr
                                <span className="text-admin-text-subtle ml-1">
                                  ({p.source})
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-admin-text-subtle mt-2">
                      {lab.manualBookingText}
                    </p>
                    {lab.bookingUrl && (
                      <a
                        href={lab.bookingUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-admin-primary underline mt-1 block truncate"
                      >
                        {lab.bookingUrl}
                      </a>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog
        open={deleteConfirmId !== null}
        onOpenChange={(open) => { if (!open) setDeleteConfirmId(null); }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Slett lab</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-admin-text-secondary mt-2">
            Er du sikker på at du vil slette denne laboratoriet? Dette kan ikke angres.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmId(null)}>
              Avbryt
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={async () => {
                if (deleteConfirmId) {
                  await handleDelete(deleteConfirmId);
                  setDeleteConfirmId(null);
                }
              }}
            >
              Slett
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-admin-text-subtle mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function MapPreview({ lat, lng }: { lat: number; lng: number }) {
  const src = `https://maps.google.com/maps?q=${lat},${lng}&z=17&output=embed`;

  return (
    <div className="rounded-lg overflow-hidden border border-admin-card-border mt-1">
      <iframe
        src={src}
        width="100%"
        height="160"
        title="Kart"
        className="block"
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
    </div>
  );
}
